import * as React from 'react';
import { styled } from '@linaria/react';

import Paper from '@/components/Paper';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  variant: 'absolute' | 'fixed';
  targetRef?: React.RefObject<HTMLDivElement>;
}

function ScrollContainer({ variant, onScroll, targetRef }: Props) {
  const ContainerBox =
    variant === 'absolute'
      ? AbsoluteContainerBox
      : PseudoFixedContainerBox;
  const Content =
    variant === 'absolute' ? BaseContent : StickyContent;

  return (
    <Wrapper onScroll={onScroll}>
      <ContainerBox>
        <Title>Containing Block</Title>
      </ContainerBox>
      <Content>
        <TargetWrapper>
          <Target ref={targetRef}>Target Element</Target>
        </TargetWrapper>
      </Content>
    </Wrapper>
  );
}

const Wrapper = styled(Paper)`
  position: relative;
  background: white;
  overflow: auto;
  height: 200px;
  outline: 2px solid;
`;

const ContainerBox = styled.div`
  border: 2px solid hsl(350deg 100% 60%);
  background: repeating-linear-gradient(
    45deg,
    hsl(350deg 100% 80% / 0.5) 0px 20px,
    hsl(350deg 100% 80% / 0.1) 20px 40px
  );
`;

const AbsoluteContainerBox = styled(ContainerBox)`
  position: absolute;
  inset: 0;
`;

const PseudoFixedContainerBox = styled(ContainerBox)`
  position: sticky;
  top: 0;
  height: 200px;
`;

const Title = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-medium);
  background: hsl(350deg 100% 60%);
  padding: 4px 16px;
  border-radius: 0 0 4px 0;
`;

const BaseContent = styled.div`
  height: 200%;
`;

const StickyContent = styled(BaseContent)`
  margin-top: -200px;
`;

const TargetWrapper = styled.div`
  height: 50%;
  display: grid;
  place-content: center;
`;

const Target = styled.div`
  --r: 16px;
  --ap: 50%;
  --ah: 8px;
  --aw: 8px;
  position: relative;
  padding: 16px;
  padding-bottom: 24px;
  background: hsl(210deg 15% 6%);
  color: white;
  font-size: 0.875rem;
  text-align: center;
  width: fit-content;
  height: fit-content;

  --border-shape: shape(
    from var(--r) 0,
    hline to calc(100% - var(--r)),
    curve to right var(--r) with right top,
    vline to calc(100% - (var(--r) + var(--ah))),
    curve to calc(100% - var(--r)) calc(100% - var(--ah)) with right
      calc(100% - var(--ah)),
    hline to calc(var(--ap) + var(--aw)),
    line by calc(var(--aw) * -1) var(--ah),
    line by calc(var(--aw) * -1) calc(var(--ah) * -1),
    hline to var(--r),
    curve to left calc(100% - (var(--r) + var(--ah))) with left
      calc(100% - var(--ah)),
    vline to var(--r),
    curve to var(--r) top with left top
  );
  clip-path: var(--border-shape);

  @supports (border-shape: shape(from 0 0, hline to 100%)) {
    clip-path: revert;
    border-shape: var(--border-shape);
    border: 2px solid white;
    box-shadow:
      0px 1px 2px hsl(0deg 0% 0% / 0.2),
      0px 3px 6px hsl(0deg 0% 0% / 0.2),
      0px 9px 18px hsl(0deg 0% 0% / 0.2),
      0px 27px 54px hsl(0deg 0% 0% / 0.2);
  }
`;

export default ScrollContainer;
